/**
 * CompositeFieldSidebar
 *
 * Editor drawer for a composite field value, opened from a cell or form field
 * via `openCompositeFieldSidebar`. Mounted by `FormPageContent` and
 * `EntitiesPageContent`, inside their `SidebarControlProvider` so entity
 * elements rendered in the body can still open a record sidebar. Visibility is
 * driven by `compositeFieldSidebarState.isOpen`, and `destroyOnHidden` unmounts
 * the body (`LayoutUI` and the cell-proxy subscribers) while closed. The drawer
 * is drag-resizable; `uiState` owns the width so it outlives the body — and this
 * component — and persists per user, and `drawerDraggerStyle` makes the drag
 * handle visible. `drawerWrapperStyle` opens it full width below antd `md`.
 *
 * The cell proxy and `onChange` handed in at open time are module-level vars in
 * `compositeFieldSidebarState` and outlive the page that supplied them, so open
 * state survives navigation and can reopen against a torn-down cell (SYD-1980
 * follow-up, pre-existing).
 *
 * `NATIVE_UNDO_DRAWER_CLASS` opts the drawer out of the form's ⌘Z: its sub-fields
 * write through this provider's parent and register no undo target.
 */

import { FC } from 'react';
import { Drawer, theme } from 'antd';
import { VerticalLeftOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { NATIVE_UNDO_DRAWER_CLASS } from '../../../use/form/useFormUndoKeybindings';
import { panelMaxWidth, setPanelWidth, uiState } from '../../../store/uiState';
import { drawerDraggerStyle } from '../../atoms/drawerDraggerStyle';
import { drawerCloseStyle } from '../../atoms/drawerCloseStyle';
import { drawerWrapperStyle } from '../../atoms/drawerWrapperStyle';
import { useCompositeFieldType } from '../useCompositeFieldType';
import {
  compositeFieldSidebarState,
  closeCompositeFieldSidebar,
  getTitle,
} from '../compositeFieldSidebarState';
import { CompositeFieldSidebarContent } from './CompositeFieldSidebarContent';

/** One Drawer per page shell, driven by the shared open state. */
export const CompositeFieldSidebar: FC = () => {
  const { token } = theme.useToken();
  const snap = useSnapshot(compositeFieldSidebarState);
  const compositeFieldType = useCompositeFieldType(
    snap.compositeFieldTypeId ?? '',
  );
  const title =
    getTitle() ??
    compositeFieldType?.description?.shortLabel ??
    'Composite Field';
  const { width } = useSnapshot(uiState.compositeFieldSidebar, { sync: true });
  const { compositeFieldTypeId, entityId, fieldId } = snap;
  const isReady =
    snap.isOpen &&
    (compositeFieldTypeId !== null || entityId !== null) &&
    fieldId !== null;

  return (
    <Drawer
      title={title}
      rootClassName={NATIVE_UNDO_DRAWER_CLASS}
      open={snap.isOpen}
      destroyOnHidden
      placement="right"
      onClose={closeCompositeFieldSidebar}
      closeIcon={<VerticalLeftOutlined />}
      size={width}
      maxSize={panelMaxWidth('compositeFieldSidebar')}
      resizable={{
        onResize: (next) => setPanelWidth('compositeFieldSidebar', next),
      }}
      styles={{
        body: { padding: 16 },
        dragger: drawerDraggerStyle(token),
        wrapper: drawerWrapperStyle(token, 'compositeFieldSidebar'),
        close: drawerCloseStyle(token),
      }}
    >
      {isReady && (
        <CompositeFieldSidebarContent
          key={fieldId}
          compositeFieldTypeId={compositeFieldTypeId}
          entityId={entityId}
          fieldId={fieldId}
          isFormulaColumn={snap.isFormulaColumn}
          tableMode={snap.tableMode}
        />
      )}
    </Drawer>
  );
};
